import { XMarkIcon } from '@heroicons/react/24/outline'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { SearchIcon } from '@/components/icons'
import { CollapsibleSection } from '@/components/shared/CollapsibleSection'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { FilterPopover } from '@/components/shared/FilterPopover'
import useAbortControllers from '@/hooks/AbortControllers'
import { getRepairOrders } from '@/services/dealer.service'
import { handlePagination, toast } from '@/utils/helpers'

/**
 * A reusable accordion section for the RO Intake page.
 * Fetches repair orders filtered by `status` values and displays them in a DataTable.
 */
export const IntakeTableSection = React.forwardRef(
  (
    {
      dealerId,
      title,
      statusValues,
      extraParams,
      columns,
      defaultOpen,
      defaultSort,
      toolbarActions,
      onRowClicked,
      showDealerClosedFilter,
    },
    ref,
  ) => {
    const navigate = useNavigate()

    const [hasOpened, setHasOpened] = useState(defaultOpen)
    const [loading, setLoading] = useState(true)

    const [repairOrders, setRepairOrders] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [perPage, setPerPage] = useState(20)
    const [totalRows, setTotalRows] = useState(0)
    const [pages, setPages] = useState(null)
    const [sortedColumn, setSortedColumn] = useState(defaultSort || 'ro_number')

    const [searchTerm, setSearchTerm] = useState('')
    const [search, setSearch] = useState(null)
    const [refreshKey, setRefreshKey] = useState(0)

    // Filter popover state — one boolean per status value, plus dealer-closed flags
    const defaultFilters = useMemo(() => {
      const base = Object.fromEntries(statusValues.map((s) => [s, false]))
      if (showDealerClosedFilter) {
        base.__dc_true = false
        base.__dc_false = false
      }
      return base
    }, [statusValues, showDealerClosedFilter])
    const [selectedFilters, setSelectedFilters] = useState(defaultFilters)

    const checkboxFilters = useMemo(() => {
      const statusCheckboxes = statusValues.map((s) => ({ key: s, label: s, name: s }))
      if (!showDealerClosedFilter) return statusCheckboxes
      return [
        ...statusCheckboxes,
        { key: '__dc_true', label: 'Dealer Closed', name: '__dc_true' },
        { key: '__dc_false', label: 'Not Dealer Closed', name: '__dc_false' },
      ]
    }, [statusValues, showDealerClosedFilter])

    const searchInputRef = useRef()
    const isInitialMount = useRef(true)

    // Cancels a superseded list request so the last page/filter *requested*
    // wins, not the last one to *resolve* (rapid pagination races).
    const { getSignal } = useAbortControllers()

    const handleErrors = useCallback((m) => toast(m, 'error'), [])

    // When any status checkboxes are checked, filter to only those; otherwise show all
    const statusParams = useMemo(() => {
      const checked = statusValues.filter((s) => selectedFilters[s])
      const active = checked.length > 0 ? checked : statusValues
      return active.map((s) => `status=${encodeURIComponent(s)}`).join('&')
    }, [selectedFilters, statusValues])

    // Dealer-closed param: both or neither checked = no filter
    const dealerClosedParam = useMemo(() => {
      if (!showDealerClosedFilter) return ''
      const wantTrue = selectedFilters.__dc_true
      const wantFalse = selectedFilters.__dc_false
      if (wantTrue && !wantFalse) return '&dealer_closed=true'
      if (wantFalse && !wantTrue) return '&dealer_closed=false'
      return ''
    }, [selectedFilters, showDealerClosedFilter])

    const BASE_URL = useMemo(
      () =>
        `/dealers/${dealerId}/repair-orders/?${statusParams}${
          extraParams ? `&${extraParams}` : ''
        }${dealerClosedParam}`,
      [dealerId, statusParams, extraParams, dealerClosedParam],
    )

    const fetchRepairOrders = useCallback(
      async (url) => {
        const response = await getRepairOrders(
          url,
          setLoading,
          handleErrors,
          () => {},
          getSignal('repair-orders'),
        )
        if (response) {
          setRepairOrders(response.results)
          setTotalRows(response.count)
          setPages({ next: response.next, previous: response.previous })
        }
        return response
      },
      [handleErrors, getSignal],
    )

    // Expose refresh method to parent via ref
    useImperativeHandle(ref, () => ({
      refresh: () => setRefreshKey((k) => k + 1),
    }))

    // Build search params
    const urlParams = useMemo(() => {
      if (search) return `q=${search}`
      return ''
    }, [search])

    // Fetch when section is opened or params change
    useEffect(() => {
      if (!hasOpened) return

      const page = isInitialMount.current ? currentPage : 1
      if (!isInitialMount.current) setCurrentPage(1)
      isInitialMount.current = false

      const params = urlParams ? `&${urlParams}` : ''
      fetchRepairOrders(
        `${BASE_URL}&limit=${perPage}&page=${page}&order_by=${sortedColumn}${params}`,
      )
    }, [hasOpened, urlParams, perPage, sortedColumn, BASE_URL, fetchRepairOrders, refreshKey])

    const searchRepairOrders = useCallback(_.debounce(setSearch, 500), [])

    return (
      <CollapsibleSection
        title={title}
        defaultOpen={defaultOpen}
        onOpenChange={(isOpen) => {
          if (isOpen && !hasOpened) {
            setHasOpened(true)
          }
        }}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-x-4">
            <TextInput
              className="w-72 pl-10"
              icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
              endIcon={
                searchTerm ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('')
                      searchRepairOrders('')
                      searchInputRef.current.value = ''
                    }}
                  >
                    <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                  </button>
                ) : null
              }
              onChange={(e) => {
                searchRepairOrders(e.target.value)
                setSearchTerm(e.target.value)
              }}
              placeholder="Search"
              value={searchTerm}
              ref={searchInputRef}
            />

            {(statusValues.length > 1 || showDealerClosedFilter) && (
              <FilterPopover
                filters={selectedFilters}
                onFiltersChange={setSelectedFilters}
                checkboxFilters={checkboxFilters}
                dropdownFilters={[]}
                defaultFilters={defaultFilters}
                ariaLabel={`Filter ${title}`}
              />
            )}
          </div>

          {toolbarActions && <div className="flex items-center gap-2">{toolbarActions}</div>}
        </div>

        <DataTable
          onRowClicked={
            onRowClicked || ((row) => navigate(`/dealer/${row.dealer}/repair-order/${row.id}`))
          }
          highlightOnHover
          columns={columns}
          data={repairOrders}
          {...getSortProps(sortedColumn, columns)}
          onChangePage={(page) =>
            handlePagination(
              page,
              currentPage,
              perPage,
              totalRows,
              pages,
              setCurrentPage,
              fetchRepairOrders,
              `${BASE_URL}&limit=`,
              `order_by=${sortedColumn}${urlParams ? `&${urlParams}` : ''}`,
            )
          }
          onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
          onSort={(column, direction) => {
            const d = direction === 'asc' ? '' : '-'
            setSortedColumn(`${d}${column.sortBy}`)
          }}
          pagination
          paginationServer
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          paginationTotalRows={totalRows}
          progressPending={loading}
          sortServer
        />
      </CollapsibleSection>
    )
  },
)

IntakeTableSection.displayName = 'IntakeTableSection'

IntakeTableSection.defaultProps = {
  defaultOpen: true,
  defaultSort: 'ro_number',
  extraParams: '',
  toolbarActions: null,
  onRowClicked: null,
  showDealerClosedFilter: false,
}

IntakeTableSection.propTypes = {
  dealerId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  statusValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  extraParams: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultOpen: PropTypes.bool,
  defaultSort: PropTypes.string,
  toolbarActions: PropTypes.node,
  onRowClicked: PropTypes.func,
  showDealerClosedFilter: PropTypes.bool,
}
